/**
 * Signals.tsx — /app/signals
 *
 * Decision signals workspace: thesis, trap, regime, volatility context.
 * "What is MER telling me to pay attention to?"
 */
import React, { useState } from 'react';
import { useStore }         from '../../store';
import { useAdaptiveSignalsVM } from '../viewModels/useAdaptiveSignalsVM';
import { ThesisPanel }      from '../../features/thesis/ThesisPanel';
import { TrapPanel }        from '../../features/traps/TrapPanel';
import { RegimePanel }      from '../../features/regime/RegimePanel';
import { SetupsPanel }      from '../../features/setups/SetupsPanel';
import { WatchlistSidebar } from '../../features/watchlist/WatchlistSidebar';

type Tab = 'thesis' | 'trap' | 'regime' | 'setups';

const TABS: { id: Tab; label: string }[] = [
  { id: 'thesis',  label: 'Thesis'  },
  { id: 'trap',    label: 'Traps'   },
  { id: 'regime',  label: 'Regime'  },
  { id: 'setups',  label: 'Setups'  },
];

export function Signals() {
  const { activeSymbol } = useStore();
  const [tab, setTab]    = useState<Tab>('thesis');
  const sym = activeSymbol ?? '';

  // Canonical post-engine adaptation — reads governance state, applies approved modifiers
  const { modifierApplied, appliedModifier, isAdvisoryMode } = useAdaptiveSignalsVM(sym);

  return (
    <div style={{ display: 'flex', minHeight: 0, flex: '1 1 0%', fontFamily: "'Courier New', monospace" }}>
      <aside style={{ width: 200, flexShrink: 0, borderRight: '1px solid #1A3050', overflowY: 'auto' }}>
        <WatchlistSidebar />
      </aside>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div style={{ display: 'flex', borderBottom: '1px solid #1A3050', flexShrink: 0 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: '8px 20px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 10, letterSpacing: 1.5, fontFamily: 'inherit',
              color: tab === t.id ? '#C9A84C' : '#3A5070',
              borderBottom: tab === t.id ? '2px solid #C9A84C' : '2px solid transparent',
            }}>
              {t.label.toUpperCase()}
            </button>
          ))}
          {/* Canonical adaptation indicator — only visible when a modifier is actually applied */}
          {modifierApplied && appliedModifier !== null && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', paddingRight: 14, gap: 6 }}>
              <span style={{ fontSize: 8, color: '#4ADE80', letterSpacing: 1 }}>● ADAPTIVE</span>
              <span style={{ fontSize: 9, color: '#4A6FA5', fontFamily: "'Courier New', monospace" }}>
                ×{appliedModifier.toFixed(3)}
              </span>
            </div>
          )}
          {isAdvisoryMode && false && null /* placeholder — advisory shows nothing */}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {sym ? (
            <>
              {tab === 'thesis'  && <ThesisPanel symbol={sym} />}
              {tab === 'trap'    && <TrapPanel   symbol={sym} />}
              {tab === 'regime'  && <RegimePanel symbol={sym} />}
              {tab === 'setups'  && <SetupsPanel symbol={sym} />}
            </>
          ) : (
            <div style={{ color: '#3A5070', textAlign: 'center', paddingTop: 60, fontSize: 11, letterSpacing: 2 }}>
              SELECT A SYMBOL
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
